import { useState } from "react";
import { FileText } from "lucide-react";
import {
  Dialog, DialogContent, DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { VoiceNotePlayer } from "./VoiceNotePlayer";
import { PdfThumbnail } from "./PdfThumbnail";
import type { ChatAttachment } from "./types";

interface Props {
  attachment: ChatAttachment;
  isOwn: boolean;
  hasCaption: boolean;
}

export function AttachmentPreview({ attachment, isOwn, hasCaption }: Props) {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const url = attachment.url;

  if (!url) {
    return (
      <div className="text-xs text-muted-foreground italic">
        Attachment unavailable
      </div>
    );
  }

  if (attachment.kind === "voice") {
    return <VoiceNotePlayer attachment={attachment} isOwn={isOwn} />;
  }

  if (attachment.kind === "image") {
    return (
      <>
        <button
          onClick={() => setLightboxOpen(true)}
          className="block overflow-hidden rounded-xl"
          aria-label="Open image"
        >
          <img
            src={url}
            alt={attachment.filename ?? "image"}
            className="block max-h-80 w-full max-w-[320px] object-cover"
            loading="lazy"
          />
        </button>
        <Dialog open={lightboxOpen} onOpenChange={setLightboxOpen}>
          <DialogContent className="max-w-4xl border-0 bg-transparent p-0">
            <DialogTitle className="sr-only">{attachment.filename ?? "Image"}</DialogTitle>
            <img src={url} alt={attachment.filename ?? "image"} className="h-auto w-full" />
          </DialogContent>
        </Dialog>
      </>
    );
  }

  // PDF / DOC tile
  const isPdf = attachment.kind === "pdf";
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className={cn(
        "flex items-center gap-3 rounded-xl px-3 py-2.5 transition-colors",
        isOwn ? "bg-background/10 hover:bg-background/15" : "bg-background hover:bg-background/85",
        hasCaption ? "" : "min-w-[240px]",
      )}
    >
      {isPdf ? (
        <PdfThumbnail url={url} />
      ) : (
        <div
          className={cn(
            "flex size-10 shrink-0 items-center justify-center rounded-lg",
            "bg-primary/10 text-foreground",
          )}
        >
          <FileText className="size-5" />
        </div>
      )}
      <div className="min-w-0 flex-1">
        <div
          className={cn(
            "truncate text-[13px] font-medium",
            isOwn ? "text-background" : "text-foreground",
          )}
        >
          {attachment.filename ?? (isPdf ? "Document.pdf" : "Document")}
        </div>
        <div
          className={cn(
            "text-[11px] uppercase tracking-wide",
            isOwn ? "text-background/60" : "text-muted-foreground",
          )}
        >
          {isPdf ? "PDF" : "DOC"}
          {attachment.byteSize ? ` · ${formatSize(attachment.byteSize)}` : ""}
        </div>
      </div>
    </a>
  );
}

function formatSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}
